//frontend/src/components/delivery/Dashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import { 
    Package, 
    Truck, 
    Box, 
    CheckCircle2, 
    ArrowLeft, 
    User, 
    Phone, 
    MapPin,
    RefreshCw,
    Clock,
    AlertTriangle,
    Loader2,
    IndianRupee,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import { reassignOrderToAdmin } from '../../actions/deliveryStaffActions';

const ReassignConfirmationModal = ({ isOpen, onClose, onConfirm, loading }) => {
    const [reason, setReason] = useState('');
    const dispatch = useDispatch();

    const handleSubmit = () => {
        if (!reason.trim()) {
            toast.error('Please provide a reason for reassignment');
            return;
        }
        onConfirm(reason);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />
                
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

                <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-sm sm:my-8 sm:align-middle sm:max-w-lg sm:w-full w-[95%] mx-auto">
                    <div className="px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
                        <div className="sm:flex sm:items-start">
                            <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                                <AlertTriangle className="w-5 h-5 text-red-600" />
                            </div>
                            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                <h3 className="text-lg font-semibold leading-6 text-gray-900 font-['Crimson_Text']">
                                    Reassign Order
                                </h3>
                                <div className="mt-4">
                                    <textarea
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626]"
                                        rows="3"
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        placeholder="Please provide the reason for reassignment"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="px-4 py-3 bg-gray-50 sm:px-6 sm:flex sm:flex-row-reverse flex flex-col-reverse sm:flex-row">
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed mb-2 sm:mb-0"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <RefreshCw className="w-4 h-4 mr-2" />
                            )}
                            {loading ? 'Processing...' : 'Reassign Order'}
                        </button>
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm mb-2 sm:mb-0"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const OrderCard = ({ order, updateOrderStatus, handleShowReassignModal, handleShowConfirmModal }) => {
    const [expanded, setExpanded] = useState(false);
    
    const getStatusIcon = (status) => {
        switch (status) {
            case 'Order Placed':
                return <Package className="w-4 h-4" />;
            case 'Order Packed':
                return <Box className="w-4 h-4" />;
            case 'Order Out for Delivery':
                return <Truck className="w-4 h-4" />;
            case 'Order Delivered':
                return <CheckCircle2 className="w-4 h-4" />;
            default:
                return <Clock className="w-4 h-4" />;
        }
    };

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'Order Placed':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'Order Packed':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'Order Out for Delivery':
                return 'bg-purple-100 text-purple-800 border-purple-200';
            case 'Order Delivered':
                return 'bg-green-100 text-green-800 border-green-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    return (
        <div className="bg-gray-50 rounded-lg p-3 sm:p-5 border border-gray-200 hover:border-[#dc2626]/20 transition-colors duration-200">
            <div className="flex flex-col gap-4">
                {/* Order Header - Always visible */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full">
                        <h3 className="text-lg font-semibold text-gray-900 font-['Crimson_Text']">
                            Order #{order.orderId}
                        </h3>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadgeColor(order.orderStatus)}`}>
                            {getStatusIcon(order.orderStatus)}
                            <span className="ml-1">{order.orderStatus}</span>
                        </span>
                    </div>
                    
                    {/* Expand/Collapse button (mobile only) */}
                    <button 
                        className="flex items-center text-gray-500 sm:hidden" 
                        onClick={() => setExpanded(!expanded)}
                    >
                        <span className="mr-1 text-sm">{expanded ? 'Hide Details' : 'View Details'}</span>
                        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                </div>
                
                {/* Order Details - always visible on desktop, toggleable on mobile */}
                <div className={`${expanded ? 'block' : 'hidden'} sm:block`}>
                    <div className="flex flex-col lg:flex-row justify-between gap-5">
                        {/* Order Details */}
                        <div className="flex-1 space-y-3 sm:space-y-5">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-5">
                                <div className="flex items-start gap-2 sm:gap-3">
                                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mt-1" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">{order.user.name}</p>
                                        <p className="text-xs text-gray-500">Customer</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-2 sm:gap-3">
                                    <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mt-1" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">{order.shippingInfo.phoneno}</p>
                                        <p className="text-xs text-gray-500">Phone</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-start gap-2 sm:gap-3">
                                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mt-1" />
                                <div>
                                    <p className="text-xs text-gray-500">Delivery Address</p>
                                    <p className="text-sm font-medium text-gray-900">
                                        {order.shippingInfo.address}, {order.shippingInfo.city} - {order.shippingInfo.postalCode}
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-2 sm:space-y-3">
                                <p className="text-sm font-medium text-gray-900">Order Items</p>
                                <div className="grid gap-2 sm:gap-3">
                                    {order.orderItems.map((item) => (
                                        <div key={item._id} className="flex items-center gap-3 sm:gap-4 bg-white p-2 sm:p-3 rounded-lg">
                                            <img
                                                src={item.image}
                                                alt={item.name}
                                                className="w-12 h-12 sm:w-14 sm:h-14 object-cover rounded-md"
                                            />
                                            <div>
                                                <p className="font-medium text-sm sm:text-base text-gray-900">{item.name}</p>
                                                <div className="flex items-center text-xs text-gray-500">
                                                    <span>{item.quantity} x</span>
                                                    <IndianRupee className="w-3 h-3 mx-1" />
                                                    <span>{item.price}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        {/* Action Buttons and Price */}
                        <div className="flex flex-col gap-3 sm:min-w-[180px]">
                            {order.orderStatus !== 'Order Delivered' && (
                                <div className="space-y-2 grid grid-cols-2 sm:grid-cols-1 gap-2">
                                    {order.orderStatus === 'Order Placed' && (
                                        <button
                                            onClick={() => updateOrderStatus(order._id, 'Order Packed')}
                                            className="w-full inline-flex items-center justify-center px-3 py-1.5 border border-transparent rounded-lg text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
                                        >
                                            <Box className="w-3.5 h-3.5 mr-1.5" />
                                            <span className="hidden sm:inline">Mark as</span> Packed
                                        </button>
                                    )}
                                    
                                    {order.orderStatus === 'Order Packed' && (
                                        <button
                                            onClick={() => updateOrderStatus(order._id, 'Order Out for Delivery')}
                                            className="w-full inline-flex items-center justify-center px-3 py-1.5 border border-transparent rounded-lg text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 transition-colors duration-200"
                                        >
                                            <Truck className="w-3.5 h-3.5 mr-1.5" />
                                            Out for Delivery
                                        </button>
                                    )}
                                    
                                    {order.orderStatus === 'Order Out for Delivery' && (
                                        <button
                                            onClick={() => handleShowConfirmModal(order._id)}
                                            className="w-full inline-flex items-center justify-center px-3 py-1.5 border border-transparent rounded-lg text-xs font-medium text-white bg-green-600 hover:bg-green-700 transition-colors duration-200"
                                        >
                                            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                                            <span className="hidden sm:inline">Mark</span> Delivered
                                        </button>
                                    )}

                                    <button
                                        onClick={() => handleShowReassignModal(order)}
                                        className="w-full inline-flex items-center justify-center px-3 py-1.5 border border-transparent rounded-lg text-xs font-medium text-white bg-yellow-600 hover:bg-yellow-700 transition-colors duration-200 col-span-2 sm:col-span-1"
                                    >
                                        <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                                        Reassign<span className="hidden sm:inline"> to Admin</span>
                                    </button>
                                </div>
                            )}
                            
                            <div className="bg-white p-3 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500">Total Amount</span>
                                    <span className="text-base font-semibold text-gray-900 flex items-center">
                                        <IndianRupee className="w-3.5 h-3.5 mr-1" />
                                        {order.totalPrice}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const DeliveryDashboard = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showReassignModal, setShowReassignModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [updating, setUpdating] = useState(false);
    
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        fetchAssignedOrders();
    }, []);

    const fetchAssignedOrders = async () => {
        try {
            const { data } = await axios.get('/api/v1/delivery/orders', {
                withCredentials: true
            });
            setOrders(data.orders || []);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error fetching orders');
        } finally {
            setLoading(false);
        }
    };

    const handleReassignOrder = async (orderId, reason) => {
        try {
            const order = orders.find(o => o._id === orderId);
            if (order?.orderStatus === 'Order Delivered') {
                toast.error('Cannot reassign delivered orders');
                return;
            }

            setUpdating(true);
            const success = await dispatch(reassignOrderToAdmin(orderId, reason));
            if (success) {
                toast.success('Order reassigned successfully');
                await fetchAssignedOrders();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error reassigning order');
        } finally {
            setUpdating(false);
            setShowReassignModal(false);
        }
    };

    const updateOrderStatus = async (orderId, status) => {
        try {
            const { data } = await axios.put(
                `/api/v1/delivery/order/${orderId}/status`,
                { orderStatus: status },
                { withCredentials: true }
            );

            if (data.success) {
                toast.success(`Order ${status.toLowerCase()} successfully`);
                await fetchAssignedOrders();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error updating order status');
        }
    };

    const handleShowConfirmModal = (orderId) => {
        setSelectedOrder({ id: orderId });
        setShowConfirmModal(true);
    };

    const handleShowReassignModal = (order) => {
        setSelectedOrder(order);
        setShowReassignModal(true);
    };

    const handleDeliveryConfirm = async () => {
        if (!selectedOrder) return;

        setUpdating(true);
        try {
            const deliveryTime = new Date().toLocaleString('en-US', {
                timeZone: 'Asia/Kolkata',
                hour12: true,
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric'
            });

            await updateOrderStatus(selectedOrder.id, 'Order Delivered');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error marking order as delivered');
        } finally {
            setUpdating(false);
            setShowConfirmModal(false);
            setSelectedOrder(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="w-12 h-12 animate-spin text-[#dc2626]" />
            </div>
        );
    }

    return (
        <div 
            className="min-h-screen py-4 sm:py-8"
            style={{
                backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
                backgroundRepeat: 'repeat',
                backgroundSize: 'auto',
                backgroundPosition: 'center'
            }}
        >
            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
                <div className="bg-white bg-opacity-95 rounded-lg shadow-sm overflow-hidden">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-3 p-3 sm:p-5 border-b border-gray-200">
                        <h1 className="text-xl sm:text-2xl font-bold text-red-600 font-['Crimson_Text'] flex items-center">
                            <Package className="w-6 h-6 sm:w-7 sm:h-7 mr-2 sm:mr-3 text-[#dc2626]" />
                            Current Deliveries
                        </h1>
                        
                        <Link
                            to="/delivery/orders/delivered"
                            className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-[#dc2626] hover:bg-[#dc2626]/90 transition-colors duration-200 w-full sm:w-auto justify-center"
                        >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            View Delivered Orders
                        </Link>
                    </div>

                    <div className="p-3 sm:p-5">
                        {orders.length > 0 ? (
                            <div className="grid gap-3 sm:gap-5">
                                {orders.map((order) => (
                                    <OrderCard 
                                        key={order._id}
                                        order={order}
                                        updateOrderStatus={updateOrderStatus}
                                        handleShowReassignModal={handleShowReassignModal}
                                        handleShowConfirmModal={handleShowConfirmModal}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 sm:py-10">
                                <Package className="mx-auto h-12 sm:h-14 w-12 sm:w-14 text-gray-400" />
                                <h3 className="mt-3 text-lg font-medium text-gray-900 font-['Crimson_Text']">No Active Orders</h3>
                                <p className="mt-2 text-sm text-gray-500">There are no orders currently assigned to you.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Delivery Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
                        <div className="fixed inset-0 transition-opacity" onClick={() => setShowConfirmModal(false)}>
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>

                        <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-sm sm:my-8 sm:align-middle sm:max-w-lg sm:w-full w-[95%] mx-auto">
                            <div className="px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 mx-auto bg-green-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg font-semibold leading-6 text-gray-900 font-['Crimson_Text']">
                                            Confirm Delivery
                                        </h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Are you sure you want to mark this order as delivered?
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="px-4 py-3 bg-gray-50 sm:px-6 sm:flex sm:flex-row-reverse flex flex-col-reverse sm:flex-row">
                                <button
                                    onClick={handleDeliveryConfirm}
                                    disabled={updating}
                                    className="inline-flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 mb-2 sm:mb-0"
                                >
                                    {updating ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <CheckCircle2 className="w-4 h-4 mr-2" />
                                    )}
                                    {updating ? 'Processing...' : 'Confirm Delivery'}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowConfirmModal(false);
                                        setSelectedOrder(null);
                                    }}
                                    disabled={updating}
                                    className="inline-flex justify-center w-full px-4 py-2 mt-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm mb-2 sm:mb-0"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reassign Modal */}
            <ReassignConfirmationModal
                isOpen={showReassignModal}
                onClose={() => {
                    setShowReassignModal(false);
                    setSelectedOrder(null);
                }}
                onConfirm={(reason) => handleReassignOrder(selectedOrder?._id, reason)}
                loading={updating}
            />
        </div>
    );
};

export default DeliveryDashboard;